import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../auth/AuthContext';
import { getFriends, removeFriend, getPendingFriendRequests, acceptFriendRequest, declineFriendRequest, getUserLeagues, getUserStats, getUserLibrary } from '../api/platformApi';
import { getUnreadCount } from '../api/messagingApi';
import AddFriendModal from '../components/account/AddFriendModal';
import MessageInbox from '../components/messaging/MessageInbox';
import MessageNotificationBadge from '../components/messaging/MessageNotificationBadge';
import SendCannedModal from '../components/messaging/SendCannedModal';
import BugReportModal from '../components/messaging/BugReportModal';
import { getGearImageUrl, handleGearImageError } from '../utils/GearImageHelpers';
import './AccountPage.css';

/**
 * AccountPage - User account overview with recent battles and friends
 *
 * @param {Object} props
 * @param {Function} props.setPage - Navigation function
 * @param {Object} props.user - Current user data
 * @param {Function} props.onLogout - Logs the user out (AppRouter owns the full
 *        logout flow: analytics reset, AuthProvider logout, state/storage cleanup)
 */
function AccountPage({ setPage, user, onLogout }) {
    // Balance is owned by AuthContext (AuthProvider) — read from the global source
    // of truth instead of fetching independently, so it stays in sync with ShopPage
    // and MyTeamsPage without a separate network call.
    const { mythrilBalance } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState('overview');
    const [showTutorial, setShowTutorial] = useState(() => {
        // Default to true (show tutorials), unless explicitly disabled
        const tutorialsDisabled = localStorage.getItem('tutorials_disabled');
        return tutorialsDisabled !== 'true';
    });
    
    // Friends state
    const [friends, setFriends] = useState([]);
    const [isLoadingFriends, setIsLoadingFriends] = useState(true);
    const [friendsError, setFriendsError] = useState(null);
    const [isAddFriendModalOpen, setIsAddFriendModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Pending requests state
    const [pendingRequests, setPendingRequests] = useState([]);
    const [isLoadingPending, setIsLoadingPending] = useState(false);
    const [pendingError, setPendingError] = useState(null);
    const [friendsTab, setFriendsTab] = useState('friends'); // 'friends' or 'pending'
    const [processingRequestId, setProcessingRequestId] = useState(null);
    const [friendActionError, setFriendActionError] = useState(null);
    
    // Recent battles state
    const [recentBattles, setRecentBattles] = useState([]);
    const [isLoadingBattles, setIsLoadingBattles] = useState(true);
    const [battlesError, setBattlesError] = useState(null);
    
    // User stats state
    const [userStats, setUserStats] = useState(null);
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [statsError, setStatsError] = useState(null);
    
    // Library state
    const [libraryCards, setLibraryCards] = useState([]);
    const [isLoadingLibrary, setIsLoadingLibrary] = useState(true);
    const [libraryError, setLibraryError] = useState(null);

    // Messaging state
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const [isSendCannedModalOpen, setIsSendCannedModalOpen] = useState(false);
    const [selectedFriend, setSelectedFriend] = useState(null);
    const [isBugReportModalOpen, setIsBugReportModalOpen] = useState(false);

    // ERROR LOUDLY: Validate user data is present
    if (!user) {
        throw new Error('AccountPage: user prop is required but not provided');
    }
    
    if (!user.username) {
        throw new Error(`AccountPage: user.username is missing - user object: ${JSON.stringify(user)}`);
    }

    // Fetch user's friends list
    useEffect(() => {
        const fetchFriends = async () => {
            try {
                setIsLoadingFriends(true);
                setFriendsError(null);
                const data = await getFriends({ limit: 5 });
                setFriends(data.friends || []);
            } catch (error) {
                console.error('AccountPage: Error fetching friends:', error);
                setFriendsError(error.message || 'Failed to load friends');
                setFriends([]);
            } finally {
                setIsLoadingFriends(false);
            }
        };
        fetchFriends();
    }, []);

    // Fetch user's recent battles
    useEffect(() => {
        const fetchRecentBattles = async () => {
            try {
                setIsLoadingBattles(true);
                setBattlesError(null);
                const data = await getUserLeagues();
                
                // VALIDATE RESPONSE - NO SILENT FAILURES
                if (!data.leagues) {
                    throw new Error('❌ AccountPage: Missing leagues in response');
                }
                
                if (!Array.isArray(data.leagues)) {
                    throw new Error(`❌ AccountPage: leagues is not an array - got ${typeof data.leagues}`);
                }
                
                // Transform backend data to match UI structure
                const transformedBattles = data.leagues.map((league, index) => {
                    // VALIDATE each league has required fields
                    if (!league.league_id) {
                        throw new Error(`❌ AccountPage: League missing league_id at index ${index}`);
                    }
                    if (!league.league_name) {
                        throw new Error(`❌ AccountPage: League ${league.league_id} missing league_name`);
                    }
                    if (league.final_rank === undefined || league.final_rank === null) {
                        throw new Error(`❌ AccountPage: League ${league.league_id} missing final_rank`);
                    }
                    if (league.is_winner === undefined || league.is_winner === null) {
                        throw new Error(`❌ AccountPage: League ${league.league_id} missing is_winner`);
                    }
                    
                    return {
                        id: league.league_id,
                        name: league.league_name,
                        subtitle: league.league_type === 'PRIVATE' ? 'Private League' : 'Public League',
                        placement: `${league.final_rank}${getOrdinalSuffix(league.final_rank)}`,
                        isWin: league.is_winner,
                        isOutlined: index === 0 // First (most recent) battle is outlined
                    };
                });
                
                setRecentBattles(transformedBattles);
            } catch (error) {
                console.error('AccountPage: Error fetching recent battles:', error);
                setBattlesError(error.message || 'Failed to load recent battles');
                setRecentBattles([]);
            } finally {
                setIsLoadingBattles(false);
            }
        };
        
        fetchRecentBattles();
    }, []);
    
    // Fetch user stats
    useEffect(() => {
        const fetchUserStats = async () => {
            try {
                setIsLoadingStats(true);
                setStatsError(null);
                const data = await getUserStats();
                
                // VALIDATE RESPONSE - NO SILENT FAILURES
                if (!data) {
                    throw new Error('❌ AccountPage: getUserStats returned null/undefined');
                }
                
                if (data.wins === undefined || data.top_3_finishes === undefined ||
                    data.top_score_ever === undefined || data.top_player_score_ever === undefined) {
                    throw new Error(`❌ AccountPage: Missing fields in stats response - got ${JSON.stringify(data)}`);
                }
                
                setUserStats(data);
            } catch (error) {
                console.error('AccountPage: Error fetching user stats:', error);
                setStatsError(error.message || 'Failed to load user stats');
                setUserStats(null);
            } finally {
                setIsLoadingStats(false);
            }
        };
        
        fetchUserStats();
    }, []);
    
    // Fetch user's drafted-card library
    useEffect(() => {
        const fetchLibrary = async () => {
            try {
                setIsLoadingLibrary(true);
                setLibraryError(null);
                const data = await getUserLibrary();

                // VALIDATE RESPONSE - NO SILENT FAILURES
                if (!Array.isArray(data.cards)) {
                    throw new Error(`❌ AccountPage: library cards is not an array - got ${typeof data.cards}`);
                }

                // Validate each card has required fields
                data.cards.forEach((card, index) => {
                    if (!card.card_name) {
                        throw new Error(`❌ AccountPage: Library card missing card_name at index ${index}`);
                    }
                    if (card.card_type !== 'PLAYER' && card.card_type !== 'GEAR') {
                        throw new Error(`❌ AccountPage: Library card "${card.card_name}" has invalid card_type "${card.card_type}"`);
                    }
                    if (!card.rarity_tier) {
                        throw new Error(`❌ AccountPage: Library card "${card.card_name}" missing rarity_tier`);
                    }
                    if (card.times_drafted === undefined || card.times_drafted === null) {
                        throw new Error(`❌ AccountPage: Library card "${card.card_name}" missing times_drafted`);
                    }
                    if (card.card_type === 'PLAYER' && !card.gsis_id) {
                        throw new Error(`❌ AccountPage: PLAYER library card "${card.card_name}" missing gsis_id (stable identity)`);
                    }
                    if (card.card_type === 'GEAR' && (card.gear_catalog_pk === undefined || card.gear_catalog_pk === null)) {
                        throw new Error(`❌ AccountPage: GEAR library card "${card.card_name}" missing gear_catalog_pk (stable identity)`);
                    }
                });

                setLibraryCards(data.cards);
            } catch (error) {
                console.error('AccountPage: Error fetching card library:', error);
                setLibraryError(error.message || 'Failed to load card library');
                setLibraryCards([]);
            } finally {
                setIsLoadingLibrary(false);
            }
        };

        fetchLibrary();
    }, []);

    // Fetch unread message count
    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const data = await getUnreadCount();
                setUnreadMessageCount(data.unread_count || 0);
            } catch (error) {
                console.error('AccountPage: Error fetching unread count:', error);
                setUnreadMessageCount(0);
            }
        };
        
        fetchUnreadCount();
        
        // Poll for unread count every 30 seconds
        const interval = setInterval(fetchUnreadCount, 30000);
        
        return () => clearInterval(interval);
    }, []);
    
    // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
    const getOrdinalSuffix = (num) => {
        const j = num % 10;
        const k = num % 100;
        if (j === 1 && k !== 11) {
            return 'st';
        }
        if (j === 2 && k !== 12) {
            return 'nd';
        }
        if (j === 3 && k !== 13) {
            return 'rd';
        }
        return 'th';
    };

    // Fetch pending requests when pending tab is selected
    useEffect(() => {
        if (friendsTab === 'pending') {
            fetchPendingRequests();
        }
    }, [friendsTab]);

    const fetchPendingRequests = async () => {
        try {
            setIsLoadingPending(true);
            setPendingError(null);
            const data = await getPendingFriendRequests();
            setPendingRequests(data.pending_requests || []);
        } catch (error) {
            console.error('AccountPage: Error fetching pending requests:', error);
            setPendingError(error.message || 'Failed to load pending requests');
            setPendingRequests([]);
        } finally {
            setIsLoadingPending(false);
        }
    };

    const handleBackClick = () => {
        if (!setPage) {
            throw new Error('AccountPage: setPage prop is required but not provided');
        }
        setPage('myTeams');
    };

    const handleCurrencyClick = () => {
        if (!setPage) {
            throw new Error('AccountPage: setPage prop is required but not provided');
        }
        setPage('storePage');
    };

    const handleUserAvatarClick = () => {
        // Already on account page, do nothing
        console.log('AccountPage: Already on account page');
    };

    const handleTabClick = (tabName) => {
        const validTabs = ['overview', 'settings', 'stats', 'messages', 'library'];
        if (!validTabs.includes(tabName)) {
            throw new Error(`AccountPage: Invalid tab name "${tabName}" - must be one of: ${validTabs.join(', ')}`);
        }
        setActiveTab(tabName);
    };

    const handleTutorialToggle = (e) => {
        const isChecked = e.target.checked;
        setShowTutorial(isChecked);
        
        // Store in localStorage (inverted logic - tutorials_disabled is opposite of showTutorial)
        if (isChecked) {
            localStorage.removeItem('tutorials_disabled');
            console.log('AccountPage: Tutorials enabled');
        } else {
            localStorage.setItem('tutorials_disabled', 'true');
            console.log('AccountPage: Tutorials disabled');
        }
    };

    const handleAddFriendClick = () => {
        setSearchQuery('');
        setIsAddFriendModalOpen(true);
    };

    const handleSearchFriends = (e) => {
        if (e.key === 'Enter' && searchQuery.trim()) {
            setIsAddFriendModalOpen(true);
        }
    };

    const handleFriendAdded = async (publicId, username) => {
        console.log(`AccountPage: Friend request sent to ${username}`);
        // Note: With friend requests, they won't appear in friends list until accepted
        // No need to refresh friends list here
    };

    const handleAcceptRequest = async (publicId, username) => {
        setProcessingRequestId(publicId);
        setFriendActionError(null);
        
        try {
            await acceptFriendRequest(publicId);
            console.log(`AccountPage: Accepted friend request from ${username}`);
            
            // Remove from pending requests
            setPendingRequests(prev => prev.filter(r => r.public_id !== publicId));
            
            // Refresh friends list to show new friend
            try {
                const data = await getFriends({ limit: 5 });
                setFriends(data.friends || []);
            } catch (error) {
                console.error('AccountPage: Error refreshing friends after accept:', error);
            }
        } catch (error) {
            console.error('AccountPage: Error accepting friend request:', error);
            setFriendActionError({
                publicId,
                action: 'accept',
                message: error.message || 'Failed to accept friend request. Please try again.',
            });
        } finally {
            setProcessingRequestId(null);
        }
    };

    const handleDeclineRequest = async (publicId, username) => {
        if (!window.confirm(`Decline friend request from ${username}?`)) {
            return;
        }

        setProcessingRequestId(publicId);
        setFriendActionError(null);
        
        try {
            await declineFriendRequest(publicId);
            console.log(`AccountPage: Declined friend request from ${username}`);
            
            // Remove from pending requests
            setPendingRequests(prev => prev.filter(r => r.public_id !== publicId));
        } catch (error) {
            console.error('AccountPage: Error declining friend request:', error);
            setFriendActionError({
                publicId,
                action: 'decline',
                message: error.message || 'Failed to decline friend request. Please try again.',
            });
        } finally {
            setProcessingRequestId(null);
        }
    };

    const handleRemoveFriend = async (publicId, username) => {
        if (!window.confirm(`Are you sure you want to remove ${username} from your friends list?`)) {
            return;
        }

        setProcessingRequestId(publicId);
        setFriendActionError(null);

        try {
            await removeFriend(publicId);
            console.log(`AccountPage: Friend ${username} removed successfully`);
            
            // Update local state immediately
            setFriends(prevFriends => prevFriends.filter(f => f.public_id !== publicId));
        } catch (error) {
            console.error('AccountPage: Error removing friend:', error);
            setFriendActionError({
                publicId,
                action: 'remove',
                message: error.message || 'Failed to remove friend. Please try again.',
            });
        } finally {
            setProcessingRequestId(null);
        }
    };

    // PR1: friend list no longer carries an integer user id — messaging is
    // still keyed on integers and is owned by PR4. Disabled here rather than
    // wired to a fabricated identifier; re-enabled once PR4 lands the
    // public_id-keyed conversation contract.
    const handleMessageFriend = (friendPublicId, username) => {
        console.log(`AccountPage: Message temporarily unavailable for friend ${username} (PR4 pending)`);
    };
    
    const handleOpenBugReportModal = () => {
        setIsBugReportModalOpen(true);
    };

    const handleLogoutClick = () => {
        if (!onLogout) {
            throw new Error('AccountPage: onLogout prop is required but not provided - cannot log the user out');
        }
        onLogout();
    };
    
    const handleUnreadCountChange = (count) => {
        setUnreadMessageCount(count);
    };

    // Calculate online friends count
    const onlineFriendsCount = 0; // Placeholder - will be implemented when we have online status

    return (
        <div className="account-page">
            {/* Header */}
            <div className="page-header">
                <button className="header-back-button" onClick={handleBackClick}>
                    <span className="back-arrow">◀</span>
                    <span className="back-label">LEAGUES</span>
                </button>

                <div className="header-right-group">
                    <button className="header-currency-button" onClick={handleCurrencyClick}>
                        <img src="/art/diamond_400_white_bg.svg" alt="Diamond" className="diamond-icon" />
                        <span className="currency-label">
                            {mythrilBalance} MYTHRIL
                        </span>
                    </button>

                    <img 
                        src="/art/UserIcon.png" 
                        alt="User" 
                        className="header-user-avatar" 
                        onClick={handleUserAvatarClick}
                        style={{ cursor: 'default' }}
                    />
                </div>
            </div>

            {/* Username Display Section */}
            <div className="username-section">
                <h1 className="username-title">{user.username}</h1>
            </div>

            {/* Main Content */}
            <div className="main-content-layout">
                {/* Sub-navigation tabs */}
                <div className="sub-navigation">
                    <div
                        className={activeTab === 'overview' ? 'tab-state-active' : 'tab-state-default'}
                        onClick={() => handleTabClick('overview')}
                    >
                        <span className={activeTab === 'overview' ? 'tab-text-active' : 'tab-text-default'}>
                            Overview
                        </span>
                    </div>
                    <div
                        className={activeTab === 'settings' ? 'tab-state-active' : 'tab-state-default'}
                        onClick={() => handleTabClick('settings')}
                    >
                        <span className={activeTab === 'settings' ? 'tab-text-active' : 'tab-text-default'}>
                            Settings
                        </span>
                    </div>
                    <div
                        className={activeTab === 'stats' ? 'tab-state-active' : 'tab-state-default'}
                        onClick={() => handleTabClick('stats')}
                    >
                        <span className={activeTab === 'stats' ? 'tab-text-active' : 'tab-text-default'}>
                            Stats
                        </span>
                    </div>
                    <div
                        className={activeTab === 'messages' ? 'tab-state-active' : 'tab-state-default'}
                        onClick={() => handleTabClick('messages')}
                    >
                        <span className={activeTab === 'messages' ? 'tab-text-active' : 'tab-text-default'}>
                            Messages
                            {unreadMessageCount > 0 && (
                                <MessageNotificationBadge count={unreadMessageCount} />
                            )}
                        </span>
                    </div>
                    <div
                        className={activeTab === 'library' ? 'tab-state-active' : 'tab-state-default'}
                        onClick={() => handleTabClick('library')}
                    >
                        <span className={activeTab === 'library' ? 'tab-text-active' : 'tab-text-default'}>
                            Library
                        </span>
                    </div>
                </div>

                {/* Conditional Content Based on Active Tab */}
                {activeTab === 'overview' && (
                    <div className="split-dashboard">
                        {/* Left panel - Recent Battles */}
                        <div className="left-panel-matches">
                            <div className="panel-header">
                                <span className="panel-header-title">Recent Battles</span>
                                <span className="panel-header-link">View All History</span>
                            </div>

                            <div className="matches-list">
                                {isLoadingBattles ? (
                                    <div className="friends-loading">
                                        <div className="loading-spinner"></div>
                                        <p>Loading recent battles...</p>
                                    </div>
                                ) : battlesError ? (
                                    <div className="friends-error">
                                        <p>Failed to load recent battles</p>
                                        <p className="error-details">{battlesError}</p>
                                    </div>
                                ) : recentBattles.length === 0 ? (
                                    <div className="friends-empty">
                                        <p className="friends-empty-main">Join a league, they are fun!</p>
                                    </div>
                                ) : (
                                    recentBattles.map((battle) => (
                                        <div
                                            key={battle.id}
                                            className={battle.isOutlined ? 'match-card-outlined' : 'match-card'}
                                        >
                                            <div className="page-cell">
                                                <div className="cell-thumbnail" />
                                                <div className="cell-meta">
                                                    <div className="cell-meta-name">{battle.name}</div>
                                                    <div className="cell-meta-subtitle">{battle.subtitle}</div>
                                                </div>
                                                <div className="cell-status-wrap">
                                                    <div className={`status-dot ${battle.isWin ? 'status-dot-win' : 'status-dot-loss'}`} />
                                                    <span className={battle.isWin ? 'status-label-win' : 'status-label-loss'}>
                                                        {battle.placement}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Right panel - Friends */}
                        <div className="right-panel-friends">
                            <div className="friends-header">
                                <div className="friends-tabs">
                                    <button
                                        className={friendsTab === 'friends' ? 'friends-tab friends-tab-active' : 'friends-tab'}
                                        onClick={() => setFriendsTab('friends')}
                                    >
                                        Friends ({onlineFriendsCount} online)
                                    </button>
                                    <button
                                        className={friendsTab === 'pending' ? 'friends-tab friends-tab-active' : 'friends-tab'}
                                        onClick={() => setFriendsTab('pending')}
                                    >
                                        Pending {pendingRequests.length > 0 && `(${pendingRequests.length})`}
                                    </button>
                                </div>
                                <svg
                                    className="user-plus-icon"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    onClick={handleAddFriendClick}
                                    style={{ cursor: 'pointer' }}
                                    title="Add Friend"
                                >
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <line x1="19" y1="8" x2="19" y2="14"></line>
                                    <line x1="22" y1="11" x2="16" y2="11"></line>
                                </svg>
                            </div>

                            <div className="friends-search-bar">
                                <svg
                                    className="search-icon"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <path d="m21 21-4.35-4.35"></path>
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search friends..."
                                    className="search-input"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={handleSearchFriends}
                                />
                            </div>

                            <div className="friends-list">
                                {friendsTab === 'friends' ? (
                                    isLoadingFriends ? (
                                        <div className="friends-loading">
                                            <div className="loading-spinner"></div>
                                            <p>Loading friends...</p>
                                        </div>
                                    ) : friendsError ? (
                                        <div className="friends-error">
                                            <p>{friendsError}</p>
                                        </div>
                                    ) : friends.length === 0 ? (
                                        <div className="friends-empty">
                                            <p className="friends-empty-main">You could use a friend</p>
                                            <button
                                                className="friends-empty-add-button"
                                                onClick={handleAddFriendClick}
                                            >
                                                Add Friend
                                            </button>
                                        </div>
                                    ) : (
                                        friends.map((friend) => {
                                            const recentLeague = friend.recent_leagues && friend.recent_leagues.length > 0
                                                ? friend.recent_leagues[0]
                                                : null;
                                            
                                            const statusText = recentLeague
                                                ? `Last played: ${recentLeague.league_name}`
                                                : 'No recent games';

                                            return (
                                                <div key={friend.public_id} className="friend-row">
                                                    <div className="friend-identity-wrap">
                                                        <div className="avatar-ring-stack">
                                                            <div className="avatar-placeholder" />
                                                            <div className="status-dot-ring status-offline" />
                                                        </div>
                                                        <div className="friend-meta">
                                                            <div className="friend-username">{friend.username}</div>
                                                            <div className="friend-status">{statusText}</div>
                                                        </div>
                                                    </div>
                                                    <div className="friend-actions-group">
                                                        <button
                                                            className="friend-button friend-button-primary"
                                                            onClick={() => handleMessageFriend(friend.public_id, friend.username)}
                                                            disabled
                                                            title="Messaging is temporarily unavailable while friend identity migrates to the new contract (PR4)"
                                                        >
                                                            Message
                                                        </button>
                                                        <button
                                                            className="friend-remove-button"
                                                            onClick={() => handleRemoveFriend(friend.public_id, friend.username)}
                                                            disabled={processingRequestId === friend.public_id}
                                                            title="Remove friend"
                                                        >
                                                            {processingRequestId === friend.public_id ? '...' : '✕'}
                                                        </button>
                                                    </div>
                                                    {friendActionError?.publicId === friend.public_id && (
                                                        <p className="friend-action-error" role="alert">
                                                            {friendActionError.message}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )
                                ) : (
                                    isLoadingPending ? (
                                        <div className="friends-loading">
                                            <div className="loading-spinner"></div>
                                            <p>Loading pending requests...</p>
                                        </div>
                                    ) : pendingError ? (
                                        <div className="friends-error">
                                            <p>{pendingError}</p>
                                            <button onClick={fetchPendingRequests}>Retry</button>
                                        </div>
                                    ) : pendingRequests.length === 0 ? (
                                        <div className="friends-empty">
                                            <p className="friends-empty-main">No pending requests</p>
                                            <p className="friends-empty-hint">
                                                Friend requests you receive will appear here.
                                            </p>
                                        </div>
                                    ) : (
                                        pendingRequests.map((request) => (
                                            <div key={request.public_id} className="friend-row pending-request-row">
                                                <div className="friend-identity-wrap">
                                                    <div className="avatar-ring-stack">
                                                        <div className="avatar-placeholder" />
                                                    </div>
                                                    <div className="friend-meta">
                                                        <div className="friend-username">{request.username}</div>
                                                        <div className="friend-status">Wants to be friends</div>
                                                    </div>
                                                </div>
                                                <div className="friend-actions-group">
                                                    <button
                                                        className="friend-button friend-button-accept"
                                                        onClick={() => handleAcceptRequest(request.public_id, request.username)}
                                                        disabled={processingRequestId === request.public_id}
                                                    >
                                                        {processingRequestId === request.public_id ? 'Accepting...' : 'Accept'}
                                                    </button>
                                                    <button
                                                        className="friend-button friend-button-decline"
                                                        onClick={() => handleDeclineRequest(request.public_id, request.username)}
                                                        disabled={processingRequestId === request.public_id}
                                                    >
                                                        {processingRequestId === request.public_id ? 'Processing...' : 'Decline'}
                                                    </button>
                                                </div>
                                                {friendActionError?.publicId === request.public_id && (
                                                    <p className="friend-action-error" role="alert">
                                                        {friendActionError.message}
                                                    </p>
                                                )}
                                            </div>
                                        ))
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Settings Panel */}
                {activeTab === 'settings' && (
                    <div className="settings-panel">
                        <div className="settings-section">
                            <h2 className="settings-section-title">Display Preferences</h2>
                            <div className="settings-item">
                                <label className="settings-checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="settings-checkbox"
                                        checked={showTutorial}
                                        onChange={handleTutorialToggle}
                                    />
                                    <span className="settings-checkbox-text">Show Tutorial Screens</span>
                                </label>
                                <p className="settings-description">
                                    When enabled, tutorial popups will be shown throughout the app to help you learn features.
                                </p>
                            </div>
                        </div>

                        <div className="settings-section">
                            <h2 className="settings-section-title">Support</h2>
                            <div className="settings-item">
                                <button
                                    className="bug-report-button"
                                    onClick={handleOpenBugReportModal}
                                >
                                    Report a Bug
                                </button>
                                <p className="settings-description">
                                    Found an issue? Let us know and help us improve Mythic Football.
                                </p>
                            </div>
                        </div>

                        <div className="settings-section">
                            <h2 className="settings-section-title">Account</h2>
                            <div className="settings-item">
                                <button
                                    id="logout-button"
                                    className="logout-button"
                                    onClick={handleLogoutClick}
                                >
                                    LOG OUT
                                </button>
                                <p className="settings-description">
                                    Sign out of your account on this device.
                                </p>
                            </div>
                        </div>
                    </div>
               )}

                {/* Stats Panel */}
                {activeTab === 'stats' && (
                    <div className="stats-panel">
                        {isLoadingStats ? (
                            <div className="friends-loading">
                                <div className="loading-spinner"></div>
                                <p>Loading stats...</p>
                            </div>
                        ) : statsError ? (
                            <div className="friends-error">
                                <p>Failed to load stats</p>
                                <p className="error-details">{statsError}</p>
                            </div>
                        ) : userStats ? (
                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-label">Wins</div>
                                    <div className="stat-value">{userStats.wins}</div>
                                </div>
                                
                                <div className="stat-card">
                                    <div className="stat-label">Top 3s</div>
                                    <div className="stat-value">{userStats.top_3_finishes}</div>
                                </div>
                                
                                <div className="stat-card">
                                    <div className="stat-label">Top Score Ever</div>
                                    <div className="stat-value">
                                        {userStats.top_score_ever.toFixed(1)}
                                    </div>
                                </div>
                                
                                <div className="stat-card">
                                    <div className="stat-label">Top Player Score Ever</div>
                                    <div className="stat-value">
                                        {userStats.top_player_score_ever.toFixed(1)}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="friends-empty">
                                <p className="friends-empty-main">No stats available</p>
                                <p className="friends-empty-sub">Complete some leagues to see your stats!</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Library Panel */}
                {activeTab === 'library' && (
                    <div className="library-panel">
                        <div className="library-header">
                            <span className="panel-header-title">Card Library</span>
                            <span className="library-count">{libraryCards.length} unique cards</span>
                        </div>

                        {isLoadingLibrary ? (
                            <div className="friends-loading">
                                <div className="loading-spinner"></div>
                                <p>Loading card library...</p>
                            </div>
                        ) : libraryError ? (
                            <div className="friends-error">
                                <p>Failed to load card library</p>
                                <p className="error-details">{libraryError}</p>
                            </div>
                        ) : libraryCards.length === 0 ? (
                            <div className="friends-empty">
                                <p className="friends-empty-main">No cards yet</p>
                                <p className="friends-empty-hint">
                                    Cards you draft in leagues will appear here.
                                </p>
                            </div>
                        ) : (
                            <div className="library-scroll-window" role="list" aria-label="Drafted cards">
                                <div className="library-grid">
                                    {libraryCards.map((card) => {
                                        // PLAYER cards show position (QB/RB/WR); GEAR cards show slot label
                                        const artLabel = card.card_type === 'PLAYER' ? card.position : 'GEAR';
                                        const subtitle = card.card_type === 'PLAYER'
                                            ? card.nfl_team
                                            : (card.gear_slot || 'Gear');
                                        // Art source follows repo conventions:
                                        // players -> /art/player_images_gsis_cartoon/{gsisId}.png (same as draft pack components)
                                        // gear    -> getGearImageUrl with fallback
                                        const artSrc = card.card_type === 'PLAYER'
                                            ? (card.gsis_id ? `/art/player_images_gsis_cartoon/${card.gsis_id}.png` : null)
                                            : getGearImageUrl({ item_id: card.item_id });

                                        return (
                                            <div
                                                key={`${card.card_type}-${card.gsis_id || card.gear_catalog_pk}-${card.rarity_tier}`}
                                                role="listitem"
                                                className={`library-card library-card--${card.rarity_tier.toLowerCase()}`}
                                            >
                                                <div className="library-card__art">
                                                    {/* Text label sits behind the image and acts as the
                                                        fallback when the artwork is missing */}
                                                    <span
                                                        className={`library-card__position library-card__position--${artLabel.toLowerCase()}`}
                                                        aria-hidden="true"
                                                    >
                                                        {artLabel}
                                                    </span>
                                                    {artSrc && (
                                                        <img
                                                            className="library-card__img"
                                                            src={artSrc}
                                                            alt={card.card_name}
                                                            loading="lazy"
                                                            onError={(e) => {
                                                                // For gear cards, use handleGearImageError to show mystery-item2.png fallback
                                                                // For player cards, hide the broken image to show position text fallback
                                                                if (card.card_type === 'GEAR') {
                                                                    handleGearImageError(e);
                                                                } else {
                                                                    e.target.onerror = null;
                                                                    e.target.style.display = 'none';
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div className="library-card__meta">
                                                    <div className="library-card__name">{card.card_name}</div>
                                                    <div className="library-card__team">{subtitle}</div>
                                                    <div className={`library-card__rarity library-card__rarity--${card.rarity_tier.toLowerCase()}`}>
                                                        {card.rarity_tier}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Messages Panel */}
                {activeTab === 'messages' && (
                    <div className="messages-panel">
                        <MessageInbox
                            onUnreadCountChange={handleUnreadCountChange}
                            onReportBug={handleOpenBugReportModal}
                        />
                    </div>
                )}
            </div>

            {/* Add Friend Modal */}
            <AddFriendModal
                isOpen={isAddFriendModalOpen}
                onClose={() => setIsAddFriendModalOpen(false)}
                onFriendAdded={handleFriendAdded}
                initialQuery={searchQuery}
                currentUserPublicId={user.public_id || null}
            />

            {/* Send Canned Message Modal */}
            <SendCannedModal
                isOpen={isSendCannedModalOpen}
                onClose={() => {
                    setIsSendCannedModalOpen(false);
                    setSelectedFriend(null);
                }}
                friendId={selectedFriend?.id}
                friendUsername={selectedFriend?.username}
            />

            {/* Bug Report Modal */}
            <BugReportModal
                isOpen={isBugReportModalOpen}
                onClose={() => setIsBugReportModalOpen(false)}
            />
        </div>
    );
}

export default AccountPage;
